import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { HStack, VStack, Spinner, Center } from "@chakra-ui/react";
import { getHistory } from "../../../../../../utils/api/history.api";
import { useQuery } from "@tanstack/react-query";
import { useUserInfo } from "../../../../../../utils/hooks/useUserInfo";
import InfiniteScroll from "react-infinite-scroll-component";
import NoItem from "../NoItem";
import SlideRange from "../../../../../common/SlideRange";
import SingleEmailCard from "./SingleEmailCard";
import {
  useHistoryDispatch,
  useHistoryState,
} from "../../../../../../utils/context/HistoryContext";
import Export from "../Export";

type EmailProps = {
  setClickedRecord: Dispatch<SetStateAction<any>>;
  clickedRow: any;
};

function Emails({ setClickedRecord, clickedRow }: EmailProps) {
  const dealership = useUserInfo("dealership");
  const [emails, setEmails] = useState<any[]>([]);
  const [sliderValue, setSliderValue] = useState("30");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const { shouldRefetch } = useHistoryState();
  const dispatch = useHistoryDispatch();

  const { isLoading, refetch, isFetching } = useQuery({
    queryKey: ["EmailLog", clickedRow?.id, sliderValue, page],
    queryFn: () =>
      getHistory({
        dealershipId: dealership?.id,
        contactId: clickedRow?.id,
        type: "EmailLog",
        days: sliderValue,
        page,
      }),
    retry: false,
    onSuccess: (data) => {
      if (emails[0]?.contact_id !== clickedRow?.id) {
        setEmails(data?.data?.histories || []);
      } else {
        const filteredNewHistories = data?.data?.histories?.filter(
          (newHistory: any) =>
            !emails?.some((existing) => existing.id === newHistory.id)
        );
        setEmails((prev) => [...prev, ...filteredNewHistories]);
      }
      setTotalCount(data.data.histories_count);
    },
  });

  useEffect(() => {
    setPage(1);
    setEmails([]);
    refetch();
  }, [clickedRow?.id, refetch, sliderValue]);

  useEffect(() => {
    if (shouldRefetch) {
      setPage(1);
      setEmails([]);
      refetch();
    }

    dispatch({ type: "SET_SHOULD_REFETCH", payload: { shouldRefetch: false } });
  }, [dispatch, refetch, shouldRefetch]);

  return (
    <>
      <Export type="EmailLog" days={sliderValue} id={clickedRow?.id} />
      <HStack background="var(--grey-200)" padding="1rem" w="100%">
        <SlideRange handleClick={(value) => setSliderValue(value)} />
      </HStack>{" "}
      {isFetching && page === 1 && (
        <Center w="100%" h="calc(100vh - 375px)">
          <Spinner />
        </Center>
      )}{" "}
      {!!emails?.length && !!emails?.filter((item) => !!item.details).length ? (
        <div
          id="scrollableDivEmail"
          style={{ height: "calc(100vh - 415px)", overflow: "auto" }}
        >
          <InfiniteScroll
            dataLength={emails.length}
            next={() => {
              setPage((prev) => prev + 1);
            }}
            hasMore={emails.length < totalCount}
            loader={<h4>Loading...</h4>}
            scrollableTarget="scrollableDivEmail"
          >
            <VStack
              alignItems="flex-start"
              gap="1rem"
              padding="1rem"
              background="var(--grey-200)"
            >
              {emails?.map((item: any) => (
                <SingleEmailCard
                  item={item}
                  setClickedRecord={setClickedRecord}
                />
              ))}
            </VStack>
          </InfiniteScroll>
        </div>
      ) : (
        !isLoading && !isFetching && <NoItem />
      )}
    </>
  );
}

export default Emails;
